import {
  EmptyState,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Spinner,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import {
  HookFormSelectionList,
  HookFormSelectionListSingle,
  HookFormText,
} from 'lib-shared-hook-form';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import { useEffect, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { automationsApi, AutomationTrigger } from 'apis/automations';
import { queuesApi } from 'apis/queues';
import {
  channelOptions,
  featureFlagList,
  triggerOptions,
} from 'components/automations/components/automations.constants';
import { getComputedOptions } from 'components/automations/components/automations.utils';
import { useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { createAppModal } from 'utils/modal';
import { useAppSelector } from 'utils/redux/useAppSelector';

import styles from './automationModal.module.scss';
import {
  type AutomationModalFormValues,
  AutomationModalResultType,
} from './automationModal.types';
import { getCreatePayload, getUpdatePayload } from './automationModal.utils';

const MAX_LENGTH = 255;

/** Modal component used to create/update automations. */
export const AutomationModal = createAppModal(
  { type: 'app:automation' },
  ({ isOpen, modal, meta, hide = noop }): JSX.Element => {
    const flags = useFeatureFlag(featureFlagList);
    const notify = useNotifications();
    const { predefinedChannels = null, predefinedTrigger = null } = modal.arg;
    const form = useForm<AutomationModalFormValues>({
      defaultValues: {
        name: '',
        description: '',
        trigger: predefinedTrigger ? predefinedTrigger : null,
        channels: [...(predefinedChannels || [])],
      },
      mode: 'all',
    });

    const selectedTrigger = form.watch('trigger');
    const isMacroTrigger =
      selectedTrigger === AutomationTrigger.EXTERNAL_TRIGGER;
    const defaultQueueId = useAppSelector(
      (state) => state.settings.organization.organization.default_queue,
    );

    const isCreating = modal.arg.automationId === null;
    const isEditing = modal.arg.automationId !== null;
    const actionVerb = isCreating ? 'create' : 'update';
    const actionVerbPastTense = isCreating ? 'created' : 'updated';

    const [
      readAutomation,
      { isLoading: isAutomationLoading, isError: isAutomationError },
    ] = automationsApi.useLazyReadAutomationQuery();
    const [createAutomation] = automationsApi.useCreateAutomationMutation();
    const [updateAutomation] = automationsApi.useUpdateAutomationMutation();
    const [readQueue] = queuesApi.useLazyReadQueueQuery();

    const { automationId } = modal.arg;

    useEffect(() => {
      if (isOpen) {
        ampli.automationsFormOpened({
          mode: isCreating ? 'create' : 'update',
        });
      }
    }, [isCreating, isOpen]);

    useEffect(() => {
      if (isOpen && isEditing && automationId) {
        readAutomation({ automationId })
          .unwrap()
          .then(({ data: automation }) => {
            form.reset({
              name: automation.name,
              description: automation.description,
              trigger: automation.trigger,
              channels: automation.channels,
            });
          })
          .catch(() => {});
      }
    }, [automationId, form, isEditing, isOpen, readAutomation]);

    const resolve = (
      result: AutomationModalResultType,
      automationId: string | null,
    ) => {
      if (meta) {
        handleHide();
        meta.resolve({
          result,
          automationId,
        });
      }
    };

    const handleSubmit: SubmitHandler<AutomationModalFormValues> = async (
      values,
    ) => {
      try {
        if (isCreating) {
          const { data: { data: defaultQueue } = {} } = await readQueue({
            id: defaultQueueId,
          });
          const response = await handleCreate(
            values,
            isMacroTrigger ? null : defaultQueueId,
            isMacroTrigger ? null : defaultQueue?.name || 'default queue',
            isMacroTrigger,
          );
          if (isResponseWithData(response)) {
            notify({
              title: 'Automation created',
              text: `Automation ${response.data.data.name} has been ${actionVerbPastTense}`,
              icon: 'done',
              className: 'success',
              time: 4000,
            });
            resolve(AutomationModalResultType.CREATED, response.data.data.id);
            return;
          }
        }

        if (isEditing) {
          const response = await handleUpdate(values);
          if (isResponseWithData(response)) {
            notify({
              title: 'Automation updated',
              text: `Automation ${response.data.data.name} has been ${actionVerbPastTense}`,
              icon: 'done',
              className: 'success',
              time: 4000,
            });
            resolve(AutomationModalResultType.UPDATED, response.data.data.id);
            return;
          }
        }

        ampli.automationsFormSubmitted({
          mode: isCreating ? 'create' : 'update',
          step: '1',
        });
      } catch {
        notify({
          title: 'An error occurred',
          text: `An error occurred when trying to ${actionVerb} automation`,
          icon: 'exclamation',
          className: 'danger',
        });
      }
    };

    const handleCreate = async (
      values: AutomationModalFormValues,
      defaultQueueId: string | null,
      defaultQueueName: string | null,
      active: boolean = false,
    ) => {
      const payload = getCreatePayload(
        values,
        defaultQueueId,
        defaultQueueName,
        active,
      );
      if (payload) {
        return createAutomation(payload);
      }

      throw new Error('Unable to create automation');
    };

    const handleUpdate = async (values: AutomationModalFormValues) => {
      const payload = getUpdatePayload(automationId, values);
      if (payload) {
        return updateAutomation(payload);
      }

      throw new Error('Unable to update automation');
    };

    /** Handles hiding the modal. */
    const handleHide = () => {
      hide?.();
    };

    /** Handles cancelling the modal. */
    const handleModalCancel = () => {
      if (meta) {
        handleHide();

        meta.resolve({
          result: AutomationModalResultType.CANCEL,
          automationId: null,
        });

        ampli.automationsFormCancelled({
          mode: isCreating ? 'create' : 'update',
          step: '1',
        });
      }
    };

    const triggerOptionsComputed = useMemo(() => {
      return getComputedOptions(triggerOptions, flags);
    }, [flags]);

    const channelOptionsComputed = useMemo(() => {
      return getComputedOptions(channelOptions, flags);
    }, [flags]);

    const showTriggerSelect = isCreating && !predefinedTrigger;
    const showChannelSelect =
      isCreating &&
      !predefinedChannels &&
      selectedTrigger !== AutomationTrigger.EXTERNAL_TRIGGER;
    const isLoading = isAutomationLoading;
    const isError = isAutomationError;

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleModalCancel}
        className={styles.root}
      >
        <ModalHead
          title={isCreating ? 'Add automation' : 'Update automation'}
          onClose={handleModalCancel}
        />

        <ModalBody>
          {isLoading && <Spinner size={32} />}
          {isError && (
            <EmptyState
              title="Something went wrong"
              description="An error occurred and this content couldn't be shown."
            />
          )}
          {!isLoading && !isError && (
            <FormGrid>
              <FormField>
                <HookFormText
                  name="name"
                  label="Name"
                  control={form.control}
                  rules={{
                    required: true,
                    minLength: {
                      value: 2,
                      message: 'Must be 2 characters or more',
                    },
                    maxLength: {
                      value: MAX_LENGTH,
                      message: `Must not be more than ${MAX_LENGTH} characters`,
                    },
                  }}
                  componentProps={{
                    placeholder: 'Automation',
                  }}
                />
              </FormField>

              <FormField>
                <HookFormText
                  name="description"
                  control={form.control}
                  label="Description"
                  componentProps={{
                    placeholder: 'What does this automation do? (optional)',
                  }}
                />
              </FormField>

              {showTriggerSelect && (
                <FormField>
                  <HookFormSelectionListSingle
                    name="trigger"
                    control={form.control}
                    componentProps={{
                      placeholder: 'When should this automation run?',
                      equalityFn: (a, b) => a === b,
                      getItemTitleByValue: (value) => {
                        return (
                          triggerOptionsComputed.find(
                            (option) => option.id === value,
                          )?.name || ''
                        );
                      },
                    }}
                    label="Select a trigger"
                    rules={{
                      required: true,
                      validate: (value) => {
                        if (
                          triggerOptionsComputed.some(
                            (option) => option.id === value,
                          )
                        ) {
                          return true;
                        }

                        return 'A trigger that will start your automation is required.';
                      },
                    }}
                    className={styles.field}
                  >
                    {triggerOptionsComputed.map((option) => (
                      <SelectionList.Item key={option.id} value={option.id}>
                        {option.name}
                      </SelectionList.Item>
                    ))}
                  </HookFormSelectionListSingle>
                </FormField>
              )}

              {showChannelSelect && (
                <FormField>
                  <HookFormSelectionList
                    name="channels"
                    control={form.control}
                    componentProps={{
                      multiple: true,
                      placeholder:
                        'Which channels should this automation apply to?',
                      equalityFn: (a, b) => a === b,
                      getItemTitleByValue: (value) => {
                        return (
                          channelOptionsComputed.find(
                            (option) => option.id === value,
                          )?.name || ''
                        );
                      },
                    }}
                    label="Select channels"
                    rules={{
                      required: true,
                      validate: (value) => {
                        if (
                          channelOptionsComputed.some((option) =>
                            (value || []).includes(option.id),
                          )
                        ) {
                          return true;
                        }

                        return 'A channel is required.';
                      },
                    }}
                    className={styles.field}
                  >
                    {channelOptionsComputed.map((option) => (
                      <SelectionList.Item key={option.id} value={option.id}>
                        {option.name}
                      </SelectionList.Item>
                    ))}
                  </HookFormSelectionList>
                </FormField>
              )}
            </FormGrid>
          )}
        </ModalBody>

        <ModalFoot
          confirmOnClick={form.handleSubmit(handleSubmit)}
          cancelOnClick={handleModalCancel}
          cancelName={'Cancel'}
          confirmName={'Save automation'}
          confirmProps={{
            isLoading,
            disabled:
              isLoading ||
              form.formState.isSubmitting ||
              !form.formState.isValid,
          }}
          cancelProps={{
            disabled: isLoading || form.formState.isSubmitting,
          }}
        />
      </Modal>
    );
  },
);
